import { User } from "contexts/types/application-context-types";

/**
 * Interface para definir regras de permissão
 * Cada regra é responsável por validar um aspecto específico de permissão
 */
export interface PermissionRule {
  validate(user: User | undefined, path: string): boolean;
  name: string;
}

/**
 * Regra para verificar se o usuário está bloqueado (Vendedor)
 */
export class BlockedVendorRule implements PermissionRule {
  name = "BlockedVendor";
  private exceptionPaths = ["/usuario-bloqueado", "/login"];

  /**
   * Valida se um vendedor bloqueado pode acessar a rota
   * Nega acesso a vendedores bloqueados, exceto em caminhos específicos
   */
  validate(user: User | undefined, path: string): boolean {
    const isBlocked = !!user?.userData.accessBlocked;
    const isVendor = user?.userData.type === "PARCEIRO_VENDEDOR";
    const isException = this.exceptionPaths.includes(path);

    // Se é vendedor bloqueado e não é uma exceção, nega acesso
    if (isVendor && isBlocked && !isException) {
      return false;
    }

    return true;
  }
}

/**
 * Regra para verificar se o Master está bloqueado com licença expirada/excedida
 */
export class BlockedMasterRule implements PermissionRule {
  name = "BlockedMaster";
  private exceptionPaths = ["/confirm-licenses", "/login"];

  /**
   * Valida se um master com licença expirada/excedida pode acessar a rota
   * Nega acesso a masters bloqueados com problemas de licença, exceto em caminhos específicos
   */
  validate(user: User | undefined, path: string): boolean {
    const isBlocked = !!user?.userData.accessBlocked;
    const isMaster = user?.userData.type === "PARCEIRO_MASTER";
    const licenseExpired = !!user?.userData.customerLicenseUserExpired;
    const quantityExceeded = !!user?.userData.customerLicenseUserQtyExceeded;
    const isException = this.exceptionPaths.includes(path);

    // Se é master e tem problemas de licença e está bloqueado, nega acesso (exceto exceções)
    if (
      isMaster &&
      (licenseExpired || quantityExceeded) &&
      isBlocked &&
      !isException
    ) {
      return false;
    }

    return true;
  }

  /**
   * Adiciona um novo caminho às exceções desta regra
   */
  addExceptionPath(path: string): void {
    if (!this.exceptionPaths.includes(path)) {
      this.exceptionPaths.push(path);
    }
  }

  /**
   * Remove um caminho das exceções desta regra
   */
  removeExceptionPath(path: string): void {
    this.exceptionPaths = this.exceptionPaths.filter((p) => p !== path);
  }
}

/**
 * Repositório singleton para gerenciar regras globais
 */
export class DefaultRulesRepository {
  private static instance: DefaultRulesRepository;
  private rules: PermissionRule[] = [];

  private constructor() {
    // Inicializa com as regras padrão
    this.rules.push(new BlockedVendorRule());
    this.rules.push(new BlockedMasterRule());
  }

  /**
   * Retorna a instância única do repositório (Singleton)
   */
  static getInstance(): DefaultRulesRepository {
    if (!DefaultRulesRepository.instance) {
      DefaultRulesRepository.instance = new DefaultRulesRepository();
    }
    return DefaultRulesRepository.instance;
  }

  /**
   * Adiciona uma nova regra ao repositório
   */
  addRule(rule: PermissionRule): void {
    if (!this.rules.find((r) => r.name === rule.name)) {
      this.rules.push(rule);
    }
  }

  /**
   * Remove uma regra pelo nome
   */
  removeRule(ruleName: string): void {
    this.rules = this.rules.filter((r) => r.name !== ruleName);
  }

  /**
   * Valida usando todas as regras
   */
  validateAll(user: User | undefined, path: string): boolean {
    return this.rules.every((rule) => rule.validate(user, path));
  }

  /**
   * Retorna cópia da lista de regras
   */
  getRules(): PermissionRule[] {
    return [...this.rules];
  }
}
